import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronRight, Loader2, Inbox, AlertTriangle, Building, UserPlus, MoreVertical, Info, Users as UsersIcon, GitCompare } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MatchStatsPanel } from "@/components/MatchStatsPanel";
import { MATCH_STATS_ENABLED } from "@/config/features";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, authFetch } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { AddOrganisationUserDialog } from "@/components/organisations/AddOrganisationUserDialog";
import { toastApiError, toastApiSuccess } from "@/lib/api-toast";

type OrganisationResponse = components["schemas"]["OrganisationResponse"];
type CompanyResponse = components["schemas"]["CompanyResponse"];
type OrganisationArSettingsResponse = components["schemas"]["OrganisationArSettingsResponse"];
type UserResponse = components["schemas"]["OrganisationUserResponse"];
type PagedResponseOfUserResponse = components["schemas"]["PagedResponseOfOrganisationUserResponse"];
type OrganisationRole = components["schemas"]["OrganisationRole"];

const ROLE_OPTIONS: OrganisationRole[] = ["Member", "Admin", "Owner"];

const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" });

const BUSINESS_TYPE_LABEL: Record<string, string> = {
  SoleTrader: "Sole trader",
  LimitedCompany: "Limited company",
  LLP: "LLP",
  Partnership: "Partnership",
};
const EMPLOYEE_COUNT_LABEL: Record<string, string> = {
  OneToTen: "1–10",
  ElevenToFifty: "11–50",
  FiftyOneToTwoHundred: "51–200",
  TwoHundredToOneThousand: "201–1,000",
  OneThousandPlus: "1,000+",
};
const ANNUAL_REVENUE_LABEL: Record<string, string> = {
  Under100k: "Under 100k",
  OneHundredToFiveHundredK: "100k–500k",
  FiveHundredKToTwoM: "500k–2M",
  TwoMPlus: "2M+",
};
const AVG_INVOICES_LABEL: Record<string, string> = {
  OneToTen: "1–10 / month",
  ElevenToFifty: "11–50 / month",
  FiftyToOneHundred: "50–100 / month",
  OneHundredPlus: "100+ / month",
};
const AVG_INVOICE_VALUE_LABEL: Record<string, string> = {
  Under500: "Under 500",
  FiveHundredToFiveK: "500–5k",
  FiveKToFiftyK: "5k–50k",
  Over50k: "50k+",
};
const PAYMENT_METHOD_LABEL: Record<string, string> = {
  BankTransfer: "Bank transfer",
  Card: "Card",
  DirectDebit: "Direct debit",
  Cash: "Cash",
};

// Tabs are addressable via the hash, derived from the URL each render (no mirrored state). An unknown or missing
// hash falls back to Info, including #match-stats while MATCH_STATS_ENABLED is off.
const TAB_IDS = ["info", "users", "match-stats"] as const;
type TabId = (typeof TAB_IDS)[number];

const AVAILABLE_TAB_IDS: readonly string[] = MATCH_STATS_ENABLED
  ? TAB_IDS
  : TAB_IDS.filter((id) => id !== "match-stats");

function readTab(hash: string): TabId {
  const value = hash.replace(/^#/, "");
  return AVAILABLE_TAB_IDS.includes(value) ? (value as TabId) : "info";
}

function statusBadge(o: OrganisationResponse) {
  return o.hasBeenOnboarded
    ? { label: "Active", classes: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" }
    : { label: "Onboarding", classes: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" };
}

export function OrganisationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const tab = readTab(location.hash);
  const { activeClient, activeClientId } = useAuth();
  const isAdmin = activeClient?.role === "Owner" || activeClient?.role === "Admin";
  const [org, setOrg] = useState<OrganisationResponse | null>(null);
  const [arSettings, setArSettings] = useState<OrganisationArSettingsResponse | null>(null);
  const [users, setUsers] = useState<UserResponse[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [addUserOpen, setAddUserOpen] = useState(false);
  // Resolved separately: OrganisationResponse has companyId but not the name, and a raw guid is worse link text.
  const [company, setCompany] = useState<CompanyResponse | null>(null);

  const loadUsers = useCallback(async (orgId: string) => {
    const resp = await authFetch(`/api/proxy/v1/organisations/${encodeURIComponent(orgId)}/users?Limit=200`);
    if (!resp.ok) {
      setUsersError(resp.status === 403 ? "You don't have access to this organisation's users." : "Couldn't load users.");
      setUsers([]);
      return;
    }
    setUsersError(null);
    const body = (await resp.json()) as PagedResponseOfUserResponse;
    setUsers(body.data ?? []);
  }, []);

  useEffect(() => {
    if (!id || !activeClientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUsersError(null);
    setOrg(null);
    setArSettings(null);
    setUsers(null);
    setCompany(null);

    Promise.all([
      api.GET("/v1/organisations/{id}", { params: { path: { id } } }),
      api.GET("/v1/organisations/{organisationId}/settings/ar", { params: { path: { organisationId: id } } }),
      authFetch(`/api/proxy/v1/organisations/${encodeURIComponent(id)}/users?Limit=200`),
    ])
      .then(async ([orgResult, arResult, usersResp]) => {
        if (cancelled) return;
        if (orgResult.error || !orgResult.data) {
          setError("Couldn't load this organisation.");
          return;
        }
        setOrg(orgResult.data);

        // Best-effort and not awaited with the rest: on failure the link still renders without the name.
        if (orgResult.data.companyId) {
          void api
            .GET("/v1/companies/{companyId}", { params: { path: { companyId: orgResult.data.companyId } } })
            .then(({ data }) => { if (!cancelled && data) setCompany(data); })
            .catch(() => { /* the id is enough to link with */ });
        }

        // AR defaults live on org AR settings; best-effort, a failure shouldn't block the overview.
        if (arResult.data) setArSettings(arResult.data);
        if (!usersResp.ok) {
          setUsersError(usersResp.status === 403 ? "You don't have access to this organisation's users." : "Couldn't load users.");
          setUsers([]);
          return;
        }
        const body = (await usersResp.json()) as PagedResponseOfUserResponse;
        setUsers(body.data ?? []);
      })
      .catch(() => { if (!cancelled) setError("Network error loading organisation."); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [id, activeClientId]);

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <Breadcrumbs name={org?.name} />

      {loading ? (
        <LoadingState />
      ) : error || !org ? (
        <ErrorState message={error ?? "Organisation not found."} />
      ) : (
        <>
          <header className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-bold">{org.name}</h1>
                <Badge variant="outline" className={statusBadge(org).classes}>{statusBadge(org).label}</Badge>
              </div>
              <span className="font-mono text-xs text-muted-foreground">{org.id}</span>
              {org.companyId && (
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Building className="size-3.5 shrink-0" />
                  Belongs to
                  <Link
                    to={`/companies/${org.companyId}`}
                    className="font-medium text-foreground hover:underline"
                  >
                    {company?.name ?? "this company"}
                  </Link>
                </span>
              )}
            </div>
          </header>

          {/* Pushes rather than replaces: back should step out of a tab like out of a link. */}
          <Tabs
            value={tab}
            onValueChange={(value) => navigate(`${location.pathname}${location.search}#${value}`)}
            className="w-full"
          >
            <TabsList className="w-full justify-start rounded-none border-b border-border bg-transparent px-2">
              <TabsTrigger value="info"><Info className="size-3.5" /> Info</TabsTrigger>
              <TabsTrigger value="users"><UsersIcon className="size-3.5" /> Users</TabsTrigger>
              {MATCH_STATS_ENABLED && (
                <TabsTrigger value="match-stats"><GitCompare className="size-3.5" /> Match Stats</TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="info" className="flex flex-col gap-6 py-4">
              <Card>
                <CardHeader>
                  <CardTitle>Contact</CardTitle>
                  <CardDescription>Primary contact and address on file.</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
                  <Field label="Email" value={org.contactEmail} />
                  <Field label="Phone" value={org.phone} />
                  <Field label="Website" value={org.website} link />
                  <Field label="Country" value={org.country} mono />
                  <Field label="Address line 1" value={org.addressLine1} />
                  <Field label="Address line 2" value={org.addressLine2} />
                  <Field label="City" value={org.city} />
                  <Field label="Postal code" value={org.postalCode} mono />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Business</CardTitle>
                  <CardDescription>Industry, registration and tax details.</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
                  <Field label="Industry" value={org.industryDisplayName} />
                  <Field label="Business type" value={mapEnum(org.businessType, BUSINESS_TYPE_LABEL)} />
                  <Field label="Incorporated" value={org.incorporationYear ? String(org.incorporationYear) : null} />
                  <Field label="Company reg. no." value={org.companyRegistrationNumber} mono />
                  <Field label="Tax number" value={org.taxNumber} mono />
                  <Field label="VAT registered" value={org.vatRegistered === null || org.vatRegistered === undefined ? null : org.vatRegistered ? "Yes" : "No"} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Operations</CardTitle>
                  <CardDescription>Scale, invoicing volume and payment defaults.</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
                  <Field label="Employees" value={mapEnum(org.employeeCount, EMPLOYEE_COUNT_LABEL)} />
                  <Field label="Annual revenue" value={mapEnum(org.annualRevenue, ANNUAL_REVENUE_LABEL)} />
                  <Field label="Avg. invoices" value={mapEnum(org.avgInvoicesPerMonth, AVG_INVOICES_LABEL)} />
                  <Field label="Avg. invoice value" value={mapEnum(org.avgInvoiceValue, AVG_INVOICE_VALUE_LABEL)} />
                  <Field label="Primary payment method" value={mapEnum(org.primaryPaymentMethod, PAYMENT_METHOD_LABEL)} />
                  <Field label="Default currency" value={org.defaultCurrency} mono />
                  <Field
                    label="Payment terms"
                    value={arSettings?.defaultPaymentTermsDays != null ? `${arSettings.defaultPaymentTermsDays} days` : null}
                  />
                  <Field
                    label="Late payment fee"
                    value={arSettings?.latePaymentFee != null ? `${arSettings.latePaymentFee}%` : null}
                  />
                  <Field label="Brand colour" value={org.primaryColour} mono />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Audit</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
                  <Field label="Created" value={org.createdAt ? dateFmt.format(new Date(org.createdAt)) : null} />
                  <Field label="Updated" value={org.updatedAt ? dateFmt.format(new Date(org.updatedAt)) : null} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="users" className="py-4">
              <section className="flex flex-col gap-3">
                <div className="flex items-baseline justify-between gap-2">
                  {/* No "Users" heading here - the tab it sits behind already says so. */}
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {users?.length ?? 0} {users?.length === 1 ? "user" : "users"}
                  </span>
                  {isAdmin && (
                    <Button size="sm" onClick={() => setAddUserOpen(true)}>
                      <UserPlus className="size-3.5" /> Add user
                    </Button>
                  )}
                </div>
                {usersError ? (
                  <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                    {usersError}
                  </div>
                ) : !users || users.length === 0 ? (
                  <Empty>No users in this organisation yet.</Empty>
                ) : (
                  <div className="rounded-lg border border-border bg-card overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/40">
                          <TableHead>User</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((u) => (
                          <UserRow
                            key={u.id ?? u.email}
                            user={u}
                            organisationId={org.id!}
                            isAdmin={!!isAdmin}
                            onChanged={() => loadUsers(org.id!)}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </section>

              <AddOrganisationUserDialog
                organisationId={org.id!}
                open={addUserOpen}
                onOpenChange={setAddUserOpen}
                onCreated={() => loadUsers(org.id!)}
              />
            </TabsContent>

            {/* Radix unmounts inactive tabs, so stats are fetched only when this tab opens. */}
            {MATCH_STATS_ENABLED && (
              <TabsContent value="match-stats" className="py-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Three-way matching</CardTitle>
                    <CardDescription>
                      How much of this organisation's bill matching runs without a reviewer.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <MatchStatsPanel organisationId={org.id!} />
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </>
      )}
    </div>
  );
}

function UserRow({
  user,
  organisationId,
  isAdmin,
  onChanged,
}: {
  user: UserResponse;
  organisationId: string;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);

  async function changeRole(newRole: OrganisationRole) {
    if (!user.id) return;
    setBusy(true);
    const resp = await authFetch(`/api/proxy/v1/users/${encodeURIComponent(user.id)}/role`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "x-dolfin-organisation-id": organisationId,
      },
      body: JSON.stringify({ role: newRole }),
    });
    setBusy(false);
    if (!resp.ok) {
      await toastApiError(resp, `Couldn't change role to ${newRole}.`);
      return;
    }
    toastApiSuccess(`Role updated to ${newRole}.`, user.email);
    onChanged();
  }

  const canMutate = isAdmin && !!user.id;
  const currentRole = user.role as OrganisationRole | undefined;

  return (
    <TableRow>
      <TableCell>
        {user.id ? (
          <Link to={`/users/${user.id}`} className="flex flex-col hover:underline">
            <span className="font-medium">{user.name ?? user.email}</span>
            <span className="text-xs text-muted-foreground">{user.email}</span>
          </Link>
        ) : (
          <div className="flex flex-col">
            <span className="font-medium">{user.name ?? user.email}</span>
            <span className="text-xs text-muted-foreground">{user.email}</span>
          </div>
        )}
      </TableCell>
      <TableCell>
        {currentRole
          ? <Badge variant="outline" className="border-border bg-muted">{currentRole}</Badge>
          : <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {user.createdAt ? dateFmt.format(new Date(user.createdAt)) : "—"}
      </TableCell>
      <TableCell className="text-right">
        {canMutate && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" disabled={busy}>
                {busy ? <Spinner className="size-3.5" /> : <MoreVertical className="size-3.5" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {ROLE_OPTIONS.filter((r) => r !== currentRole).map((r) => (
                <DropdownMenuItem key={r} onClick={() => changeRole(r)}>
                  Change to {r}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  );
}

function Breadcrumbs({ name }: { name?: string }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <Link to="/organisations" className="rounded-sm hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        Organisations
      </Link>
      <ChevronRight className="size-3.5" />
      <span className="truncate text-foreground">{name ?? "…"}</span>
    </nav>
  );
}

function Field({ label, value, mono, link }: { label: string; value?: null | string; mono?: boolean; link?: boolean }) {
  const empty = value == null || value === "";
  const baseClasses = mono ? "font-mono text-xs" : "text-sm";
  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      {empty ? (
        <div className="text-sm text-muted-foreground">—</div>
      ) : link ? (
        <a
          href={value!.startsWith("http") ? value! : `https://${value}`}
          target="_blank"
          rel="noopener noreferrer"
          className={`${baseClasses} truncate text-primary hover:underline`}
        >
          {value}
        </a>
      ) : (
        <div className={`${baseClasses} truncate`}>{value}</div>
      )}
    </div>
  );
}

function mapEnum(v: null | string | undefined, dict: Record<string, string>): string | null {
  if (v == null) return null;
  return dict[v] ?? v;
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border py-16 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" /> Loading organisation…
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
      <AlertTriangle className="size-4 mt-0.5" />
      <span>{message}</span>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
      <Inbox className="size-6" />
      {children}
    </div>
  );
}
